import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  Braces,
  CheckCircle2,
  Flag,
  Lock,
  Save,
  ShieldCheck,
  Smartphone,
  Store,
  Truck,
  Users,
  Wrench,
} from 'lucide-react';
import { SkeletonPage } from '../ui/kit';
import './AppGate.css';
import PageHeader from '../components/PageHeader';

const APPS = [
  {
    key: 'customer_android',
    label: 'Kunden-App',
    platform: 'Android',
    audience: 'Kunde',
    store: 'https://play.google.com/store/apps/details?id=…',
    icon: Users,
  },
  {
    key: 'customer_ios',
    label: 'Kunden-App',
    platform: 'iOS',
    audience: 'Kunde',
    store: 'https://apps.apple.com/app/id…',
    icon: Users,
  },
  {
    key: 'driver_android',
    label: 'Fahrer-App',
    platform: 'Android',
    audience: 'Fahrer',
    store: 'https://play.google.com/store/apps/details?id=…',
    icon: Truck,
  },
  {
    key: 'driver_ios',
    label: 'Fahrer-App',
    platform: 'iOS',
    audience: 'Fahrer',
    store: 'https://apps.apple.com/app/id…',
    icon: Truck,
  },
];

const emptyApp = { min_build: 0, latest_build: 0, force_update: false, store_url: '', message: '' };

function Toggle({ on, onChange, label }) {
  return (
    <button
      type="button"
      className={`app-gate-toggle ${on ? 'is-on' : ''}`}
      onClick={() => onChange(!on)}
      aria-label={label}
      aria-pressed={on}
    >
      <span />
    </button>
  );
}

// APP-GATE-LESER-001 — die Beschriftungen sagen, was der Server WIRKLICH tut. Er kennt genau
// drei Ausgänge: nichts, den weichen Update-Hinweis (APP-UPDATE-HINWEIS-001: ein gepflegter
// aktuellster Build zeigt älteren Builds einmal einen Dialog, ohne Zwang), oder die vollflächige
// Sperre. „Update erzwingen" sperrt ausschließlich
// Builds UNTERHALB des Mindest-Builds. Wer den aktuellen Build hat, wird nie gesperrt (sonst
// sperrte sich der Betrieb selbst aus). Steht der Mindest-Build auf 0, wirkt der Schalter auf
// niemanden. Der Not-Aus für alle vier Kanäle ist und bleibt der Wartungsmodus.
const forceIsIdle = app => !!app.force_update && !(Number(app.min_build) > 0);

// Der Status-Chip zeigt die WIRKSAME Schwelle des Servers, nicht die eingetragene Zahl.
// Beide Zahlen können auseinanderlaufen: steht der Mindest-Build über dem aktuellsten Build,
// deckelt der Server ihn (N1) — die Kachel sagte dann „Sperre unter Build 1000", während erst
// unterhalb von 900 gesperrt wird. Deshalb rechnet das Portal hier NICHTS nach: `wirkung` kommt
// fertig aus derselben Quelle, die auch das Urteil fällt (backend/src/lib/appGateVerdict.js,
// geliefert von GET/PUT /admin/app-gate). Der Chip beschreibt NUR die Sperre. Den weichen
// Update-Hinweis (ausgelöst allein durch einen gepflegten aktuellsten Build) nennt die
// Feldbeschriftung; der Chip erfindet dafür keine eigene Zahl.
function statusFor(wirkung) {
  if (!wirkung) return { label: 'Wirkung unbekannt', tone: 'neutral' };
  const schwelle = Number(wirkung.effective_min_build) > 0 ? Number(wirkung.effective_min_build) : 0;
  if (wirkung.blockt) return { label: `Sperre unter Build ${schwelle}`, tone: 'danger' };
  if (wirkung.force_update) return { label: 'Zwang ohne Wirkung', tone: 'warning' };
  return { label: 'Permissiv', tone: 'neutral' };
}

// Der Chip zeigt den GESPEICHERTEN Stand — nur der wirkt. Wird eine Kachel bearbeitet und noch
// nicht gespeichert, sagt die Kachel das ausdrücklich, statt eine Zahl zu zeigen, die gerade
// nichts bedeutet. Verglichen wird bewusst normalisiert (Eingabefelder liefern Strings), und
// bewusst NUR für diesen Hinweis — eine Wirkungs-Zahl leitet das Portal daraus nie ab.
const alsZahl = wert => {
  const zahl = Math.floor(Number(wert));
  return Number.isFinite(zahl) && zahl > 0 ? zahl : 0;
};
const kanalFingerabdruck = wert => JSON.stringify({
  min_build: alsZahl(wert && wert.min_build),
  latest_build: alsZahl(wert && wert.latest_build),
  force_update: !!(wert && wert.force_update),
  store_url: String((wert && wert.store_url) || ''),
  message: String((wert && wert.message) || ''),
});

// APP-GATE-LESER-001 (Nachtrag N1/N5) — der SERVER meldet, wo er beim Urteil still korrigiert.
// Die Regel wird bewusst NICHT hier nachgerechnet: das Portal wuerde sonst eine zweite Wahrheit
// erfinden, die von der des Servers abweichen kann. Angezeigt wird, was der Server sagt.
const LABEL_FUER = key => {
  const app = APPS.find(a => a.key === key);
  return app ? `${app.label} ${app.platform}` : key;
};

function warnungsText(warning) {
  if (warning.code === 'min_build_over_latest_build') {
    return `Der Mindest-Build (${warning.min_build}) liegt über dem aktuellsten Build `
      + `(${warning.latest_build}). Bei aktivem „Update erzwingen“ gilt Build `
      + `${warning.effective_min_build} als wirksame Sperrgrenze. Ohne Update-Zwang löst `
      + `diese Grenze keine Sperre aus. Trage einen Mindest-Build ein, der höchstens so hoch ist `
      + `wie der aktuellste Build.`;
  }
  if (warning.code === 'force_update_without_latest_build') {
    return `„Update erzwingen" ist an und der Mindest-Build steht auf ${warning.min_build}, `
      + 'aber es ist kein aktuellster Build gepflegt (0). Der Server kennt damit keine neuere '
      + 'Fassung, auf die der Store-Knopf zeigen könnte — der Zwang wirkt derzeit gar nicht, '
      + 'gesperrt wird niemand. Trage den aktuellsten Build ein, damit die Sperre greift.';
  }
  if (warning.code === 'store_url_not_deliverable') {
    return 'Der Store-Link ist kein http(s)-Link und wird deshalb nicht an die App ausgeliefert. '
      + 'Die App benutzt dann ihren fest einkompilierten Store-Link.';
  }
  return 'Der Server hat diesen Kanal beanstandet.';
}

export default function AppGate() {
  const [gate, setGate] = useState(null);
  const [flagsText, setFlagsText] = useState('{}');
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState([]);
  // Die WIRKUNG kommt fertig vom Server (eine Quelle mit dem Urteil), dazu der gespeicherte
  // Stand — nur an ihm laesst sich sagen, ob eine Kachel gerade unbestaetigten Entwurf zeigt.
  const [wirkung, setWirkung] = useState(null);
  const [gespeichert, setGespeichert] = useState(null);
  const [wartungGespeichert, setWartungGespeichert] = useState(false);

  const uebernehmeServerstand = (daten, entwurfApps) => {
    // Ein Entwurf oder ein erfolgreiches HTTP ohne Wartungswert bestätigt keinen Zustand.
    // Fehlende Werte lassen den zuletzt bestätigten Serverstand unverändert.
    if (typeof daten?.gate?.maintenance === 'boolean') {
      setWartungGespeichert(daten.gate.maintenance);
    }
    setWarnings(Array.isArray(daten?.warnings) ? daten.warnings : []);
    setWirkung(daten?.wirkung && typeof daten.wirkung === 'object' ? daten.wirkung : null);
    const apps = {};
    APPS.forEach(a => {
      const vomServer = daten?.gate?.apps && daten.gate.apps[a.key];
      apps[a.key] = { ...emptyApp, ...(vomServer || (entwurfApps && entwurfApps[a.key]) || {}) };
    });
    setGespeichert(apps);
    return apps;
  };

  useEffect(() => {
    api.get('/admin/app-gate').then(r => {
      const g = r.data.gate || {};
      const apps = {};
      APPS.forEach(a => { apps[a.key] = { ...emptyApp, ...(g.apps && g.apps[a.key]) }; });
      const next = { maintenance: !!g.maintenance, message: g.message || '', flags: g.flags || {}, apps };
      setGate(next);
      uebernehmeServerstand(r.data, apps);
      setFlagsText(JSON.stringify(next.flags, null, 2));
    }).catch(() => toast.error('Konnte App-Steuerung nicht laden'));
  }, []);

  const setApp = (k, field, val) => setGate(g => ({ ...g, apps: { ...g.apps, [k]: { ...g.apps[k], [field]: val } } }));

  const save = async () => {
    let flags = {};
    try { flags = JSON.parse(flagsText || '{}'); if (typeof flags !== 'object' || Array.isArray(flags)) throw new Error(); }
    catch { return toast.error('Feature-Flags sind kein gültiges JSON-Objekt'); }
    setSaving(true);
    try {
      const payload = { gate: { ...gate, flags } };
      const antwort = await api.put('/admin/app-gate', payload);
      // Erst jetzt gilt der Entwurf — also erst jetzt darf die Kachel seine Wirkung zeigen.
      uebernehmeServerstand(antwort?.data, gate.apps);
      toast.success('App-Steuerung gespeichert');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  if (!gate) return <SkeletonPage />;

  const forcedCount = APPS.filter(app => gate.apps[app.key].force_update).length;
  // Wie viele Kanäle sperren wirklich jemanden? Das sagt der SERVER (`wirkung.blockt`) — dazu
  // gehört seit Nachtrag N1 (verschärft) auch ein gepflegter aktuellster Build, sonst sperrt
  // der Zwang niemanden. Das Portal zählt es nicht selbst nach.
  const blockingCount = APPS.filter(app => !!(wirkung && wirkung[app.key] && wirkung[app.key].blockt)).length;
  const idleForceCount = APPS.filter(app => forceIsIdle(gate.apps[app.key])).length;
  const minimumCount = APPS.filter(app => Number(gate.apps[app.key].min_build) > 0).length;
  let stagedFlags = null;
  try {
    const parsed = JSON.parse(flagsText || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) stagedFlags = parsed;
  } catch {
    stagedFlags = null;
  }
  const flagCount = stagedFlags ? Object.keys(stagedFlags).length : null;
  const wartungEntwurf = gate.maintenance !== wartungGespeichert;
  const riskActive = wartungGespeichert || blockingCount > 0;

  return (
    <div className="app-gate-page">
      <PageHeader
        title="App-Steuerung"
        description="Versionen, Update-Gates und Laufzeit-Schalter für alle VOOGO Apps kontrolliert verwalten."
        actions={
        <div className="app-gate-hero-badges" aria-label="Geltungsbereich">
          <span><Lock size={14} /> settings.manage</span>
          <span><Smartphone size={14} /> 4 App-Kanäle</span>
        </div>
      }
      />

      <section className={`app-gate-impact ${riskActive ? 'is-critical' : ''}`}>
        <div className="app-gate-impact-icon">
          {riskActive ? <AlertTriangle size={22} /> : <CheckCircle2 size={22} />}
        </div>
        <div>
          <strong>
            {wartungGespeichert
              ? 'Wartungsmodus: alle vier Kanäle sperren jede App, die ihre Build-Nummer meldet'
              : blockingCount > 0
                ? `Sperre auf ${blockingCount} von 4 Kanälen — nur für Builds unter dem Mindest-Build`
                : 'Kein Kanal sperrt gerade jemanden'}
          </strong>
          <p>
            Änderungen werden beim nächsten App-Start wirksam. „Update erzwingen“ sperrt
            ausschließlich Builds unterhalb des Mindest-Builds — und nur, solange ein
            aktuellster Build gepflegt ist, den der Store-Knopf auch anbieten kann; sonst
            passiert nichts. Ein gepflegter aktuellster Build zeigt älteren Builds außerdem
            einen weichen Update-Hinweis („Nein, später" / „Ja, jetzt") — ohne Zwang, ohne
            Sperre. Die aktuelle Version bleibt immer nutzbar. Der Not-Aus für alle ist
            der Wartungsmodus; er greift in jeder App, die ihre Build-Nummer meldet — also ab der
            nächsten Store-Fassung.
          </p>
          {idleForceCount > 0 && (
            <p className="app-gate-impact-note">
              <AlertTriangle size={14} />
              {idleForceCount === 1
                ? 'Auf 1 Kanal ist „Update erzwingen“ an, während der Mindest-Build dort auf 0 steht — dort wirkt der Schalter auf niemanden.'
                : `Auf ${idleForceCount} Kanälen ist „Update erzwingen“ an, während der Mindest-Build dort auf 0 steht — dort wirkt der Schalter auf niemanden.`}
            </p>
          )}
        </div>
        <span className={`app-gate-impact-status ${riskActive ? 'is-critical' : ''}`}>
          {riskActive ? 'Eingriff aktiv' : 'Betrieb offen'}
        </span>
      </section>

      {warnings.length > 0 && (
        <section className="app-gate-server-warnings" aria-label="Serverhinweise" data-server-warnings={warnings.length}>
          <div className="app-gate-server-warnings-head">
            <AlertTriangle size={18} />
            <strong>
              {warnings.length === 1
                ? 'Der Server korrigiert 1 Eintrag beim Urteil'
                : `Der Server korrigiert ${warnings.length} Einträge beim Urteil`}
            </strong>
          </div>
          <ul>
            {warnings.map(w => (
              <li key={`${w.app}-${w.code}`} data-warning-code={w.code} data-warning-app={w.app}>
                <strong>{LABEL_FUER(w.app)}</strong>
                <span>{warnungsText(w)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="app-gate-metrics" aria-label="Release-Übersicht">
        <article>
          <div className="app-gate-metric-icon"><Smartphone size={20} /></div>
          <div>
            <span>App-Kanäle</span>
            <strong>4</strong>
            <small>Kunde und Fahrer</small>
          </div>
        </article>
        <article>
          <div className="app-gate-metric-icon"><AlertTriangle size={20} /></div>
          <div>
            <span>Update-Zwang</span>
            <strong>{forcedCount}</strong>
            <small>
              {idleForceCount > 0
                ? `${idleForceCount} davon ohne Wirkung (Mindest-Build 0)`
                : 'sperrt nur Builds unter dem Mindest-Build'}
            </small>
          </div>
        </article>
        <article>
          <div className="app-gate-metric-icon"><ShieldCheck size={20} /></div>
          <div>
            <span>Mindest-Build</span>
            <strong>{minimumCount}</strong>
            <small>Kanäle konfiguriert</small>
          </div>
        </article>
        <article className={flagCount === null ? 'has-error' : ''}>
          <div className="app-gate-metric-icon"><Braces size={20} /></div>
          <div>
            <span>Feature-Flags</span>
            <strong>{flagCount === null ? '!' : flagCount}</strong>
            <small>{flagCount === null ? 'JSON prüfen' : 'Schlüssel im Entwurf'}</small>
          </div>
        </article>
      </section>

      <section className={`app-gate-section app-gate-maintenance ${wartungGespeichert ? 'is-active' : ''}`}>
        <div className="app-gate-section-heading">
          <div className="app-gate-heading-index">01</div>
          <div className="app-gate-heading-icon"><Wrench size={20} /></div>
          <div>
            <span>Globaler Betriebszustand</span>
            <h2>Wartungsmodus</h2>
            <p>
              Der Not-Aus: sperrt in allen vier Kanälen jede App, die ihre Build-Nummer meldet —
              ganz gleich, wie hoch sie ist. Ältere Store-Fassungen melden noch keine Build-Nummer
              und bleiben deshalb unberührt; der Wartungsmodus greift dort ab der nächsten
              Store-Fassung.
            </p>
          </div>
          <div className="app-gate-maintenance-control">
            <span>{wartungEntwurf ? 'Entwurf: ' : ''}{gate.maintenance ? 'Aktiv' : 'Inaktiv'}</span>
            <Toggle
              on={gate.maintenance}
              onChange={value => setGate(current => ({ ...current, maintenance: value }))}
              label="Wartungsmodus umschalten"
            />
          </div>
        </div>
        {wartungEntwurf && (
          <p className="app-gate-draft-note" data-maintenance-draft>
            <AlertTriangle size={14} />
            Entwurf: Wartung {gate.maintenance ? 'einschalten' : 'ausschalten'} — noch nicht gespeichert.
            {' '}Auf dem Server ist die Wartung {wartungGespeichert ? 'aktiv' : 'inaktiv'}.
          </p>
        )}
        {gate.maintenance && (
          <label className="app-gate-field app-gate-maintenance-message">
            <span>Wartungs-Nachricht <small>optional · max. 500 Zeichen serverseitig</small></span>
            <input
              value={gate.message}
              onChange={event => setGate(current => ({ ...current, message: event.target.value }))}
              placeholder="Wir sind bald wieder da …"
            />
          </label>
        )}
      </section>

      <section className="app-gate-section">
        <div className="app-gate-section-heading app-gate-channel-heading">
          <div className="app-gate-heading-index">02</div>
          <div>
            <span>Plattformen</span>
            <h2>Release-Kanäle</h2>
            <p>Jeder Kanal behält eigene Builds, Store-Verlinkung und Nachricht.</p>
          </div>
          <div className="app-gate-legend" aria-label="Statuslegende">
            <span><i className="is-open" /> permissiv</span>
            <span><i className="is-warning" /> Zwang ohne Wirkung</span>
            <span><i className="is-danger" /> Sperre unter Mindest-Build</span>
          </div>
        </div>

        <div className="app-gate-channel-grid">
          {APPS.map((app, index) => {
            const value = gate.apps[app.key];
            const status = statusFor(wirkung && wirkung[app.key]);
            const unbestaetigt = !!gespeichert
              && kanalFingerabdruck(value) !== kanalFingerabdruck(gespeichert[app.key]);
            const AudienceIcon = app.icon;
            return (
              <article className={`app-gate-channel-card is-${status.tone}`} key={app.key} data-app-key={app.key}>
                <div className="app-gate-channel-top">
                  <div className="app-gate-channel-identity">
                    <div className="app-gate-channel-number">{String(index + 1).padStart(2, '0')}</div>
                    <div className="app-gate-channel-icon"><AudienceIcon size={20} /></div>
                    <div>
                      <span>{app.audience} · {app.platform}</span>
                      <h3>{app.label}</h3>
                    </div>
                  </div>
                  <span className={`app-gate-status is-${status.tone}`}>{status.label}</span>
                </div>
                {unbestaetigt && (
                  <p className="app-gate-draft-note" data-draft={app.key}>
                    <AlertTriangle size={14} />
                    Entwurf: noch nicht gespeichert. Der Status oben zeigt, was der Server
                    GERADE tut — der Eintrag wirkt erst nach dem Speichern.
                  </p>
                )}

                <div className="app-gate-build-grid">
                  <label className="app-gate-field">
                    <span>Mindest-Build <small>Grenze der Sperre · 0 = keine</small></span>
                    <input
                      type="number"
                      min="0"
                      value={value.min_build}
                      onChange={event => setApp(app.key, 'min_build', event.target.value)}
                      aria-label={`${app.label} ${app.platform} Mindest-Build`}
                    />
                  </label>
                  <label className="app-gate-field">
                    <span>Aktuellster Build <small>ältere Builds sehen den Update-Hinweis (kein Zwang) · Pflicht für die Sperre · 0 = aus</small></span>
                    <input
                      type="number"
                      min="0"
                      value={value.latest_build}
                      onChange={event => setApp(app.key, 'latest_build', event.target.value)}
                      aria-label={`${app.label} ${app.platform} aktuellster Build`}
                    />
                  </label>
                </div>

                <label className="app-gate-field">
                  <span><Store size={14} /> Store-Link</span>
                  <input
                    type="url"
                    value={value.store_url || ''}
                    onChange={event => setApp(app.key, 'store_url', event.target.value)}
                    placeholder={app.store}
                    aria-label={`${app.label} ${app.platform} Store-Link`}
                  />
                </label>

                <label className="app-gate-field">
                  <span>Sperr-Nachricht <small>optional · ersetzt den Text auf dem Sperrbildschirm und im Update-Hinweis</small></span>
                  <input
                    value={value.message || ''}
                    onChange={event => setApp(app.key, 'message', event.target.value)}
                    placeholder="Bitte aktualisiere die App."
                    aria-label={`${app.label} ${app.platform} Sperr-Nachricht`}
                  />
                </label>

                <div className={`app-gate-force-row ${value.force_update ? 'is-active' : ''} ${forceIsIdle(value) ? 'is-idle' : ''}`}>
                  <div>
                    <AlertTriangle size={18} />
                    <span>
                      <strong>Update erzwingen</strong>
                      <small>
                        Sperrt Builds unter dem Mindest-Build — und nur, wenn ein aktuellster
                        Build gepflegt ist, den der Store-Knopf anbieten kann. Die aktuelle
                        Version bleibt immer nutzbar.
                      </small>
                    </span>
                  </div>
                  <Toggle
                    on={value.force_update}
                    onChange={next => setApp(app.key, 'force_update', next)}
                    label={`${app.label} ${app.platform} Update erzwingen`}
                  />
                </div>
                {forceIsIdle(value) && (
                  <p className="app-gate-force-note" data-idle-force={app.key}>
                    <AlertTriangle size={14} />
                    Ohne Wirkung: der Mindest-Build steht auf 0 — so sperrt der Zwang niemanden.
                    Trage den Build ein, ab dem aktualisiert werden muss.
                  </p>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="app-gate-section app-gate-flags">
        <div className="app-gate-section-heading">
          <div className="app-gate-heading-index">03</div>
          <div className="app-gate-heading-icon"><Flag size={20} /></div>
          <div>
            <span>Laufzeit-Konfiguration</span>
            <h2>Feature-Flags</h2>
            <p>Die Apps lesen dieses JSON-Objekt beim Start. Ungültige Daten werden nicht gespeichert.</p>
          </div>
          <span className={`app-gate-json-status ${flagCount === null ? 'is-invalid' : ''}`}>
            {flagCount === null ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
            {flagCount === null ? 'JSON ungültig' : 'JSON gültig'}
          </span>
        </div>
        <div className="app-gate-code-shell">
          <div className="app-gate-code-bar">
            <span><Braces size={15} /> runtime-flags.json</span>
            <small>{flagCount === null ? 'Speichern blockiert' : `${flagCount} Schlüssel`}</small>
          </div>
          <textarea
            value={flagsText}
            onChange={event => setFlagsText(event.target.value)}
            spellCheck={false}
            aria-label="Feature-Flags JSON"
          />
        </div>
      </section>

      <footer className="app-gate-savebar">
        <div>
          <ShieldCheck size={20} />
          <span>
            <strong>Systemweite Konfiguration</strong>
            <small>Ein Speichervorgang aktualisiert Wartung, alle vier Kanäle und Feature-Flags gemeinsam.</small>
          </span>
        </div>
        <button type="button" onClick={save} disabled={saving}>
          <Save size={17} />
          {saving ? 'Speichert…' : 'Konfiguration speichern'}
        </button>
      </footer>
    </div>
  );
}
